import { slugifyRole } from './salaryAggregation';

/**
 * Known cases where the LLM's free-text `normalisedTitle_en` fragments one real role across
 * several distinct strings, each of which otherwise gets its own /salary/{slug} page. Two source
 * categories, both confirmed against a full scan of active Finland listings (Aug 2026 audit):
 *
 * 1. Genuine EN synonym/seniority/word-order variants of the same role (e.g. "Team Lead" /
 *    "Team Leader", "Software Engineer" / "Senior Software Engineer" — seniority merges here are
 *    intentional, since generateSalaryStats.ts already has a separate TITLE+ROLE_LEVEL dimension
 *    for that; the TITLE-only bucket should represent the occupation, not the seniority).
 * 2. Raw Finnish (or occasionally mixed-language) text leaking into `normalisedTitle_en` instead
 *    of being translated — same bug class as the original "Lähihoitaja" find. These map to a
 *    proper English canonical, cross-referenced against existing EN buckets where one already
 *    exists (e.g. "Waiter" already had genuine EN postings) or a new standard-terminology
 *    canonical otherwise.
 *
 * Deliberately excluded: "Care Assistant" (possibly a distinct role from Practical Nurse, not
 * merged without a product decision), and pure case-only duplicates ("Open Application" / "Open
 * application") — those are handled generically by normalizeTitleCasing.ts instead of being
 * hand-listed here, since case drift isn't specific to any one title.
 *
 * The merge-candidate generation itself went through a validation pass: an earlier looser
 * algorithm (string-similarity ratio, or "shares every non-generic word") produced real false
 * positives — "Product Manager"/"Production Manager", "Site Manager"/"Store Manager", "AI
 * Engineer"/"QA Engineer", "Excavator Operator"/"Excavator Foreman" — all confidently flagged as
 * duplicates by the loose version, all actually distinct roles. Every entry below survived a
 * stricter check (exact match modulo case; one title a literal prefix of the other plus a short
 * suffix; a single known Finnish-diacritic substitution; or, for word-reordering/modifier cases,
 * requiring the FULL set of role-defining words to match exactly rather than a subset).
 *
 * Keyed case-insensitively; the canonical key's own casing is what every variant maps to.
 */
const SALARY_ROLE_CANONICAL_ALIASES: Record<string, string[]> = {
  'Practical Nurse': [
    'Healthcare Assistant',
    'Lähihoitaja',
    'Lähihoitajat',
    'Nurse Assistant',
    'Nursing Assistant',
    'Assistant Nurse',
  ],
  'Personal Assistant': ['Personal Assistants', 'Personal assistant'],
  'Construction Worker': ['Construction Workers'],
  'Electrician': ['Electricians', 'Sähköasentaja'],
  'Electrical Installer': ['Electrical Installers'],
  'Internship': ['Intern'],
  'Team Leader': ['Team Lead'],
  'Coach': ['Coaches'],
  'Engineer': ['Engineering'],
  'Software Engineer': ['Senior Software Engineer'],
  'Project Manager': ['Senior Project Manager'],
  'Chef': ['Head Chef'],
  'Excavator Operator': ['Kaivinkoneenkuljettaja', 'Kokenut kaivinkoneenkuljettaja', 'kaivinkoneenkuljettaja'],
  'Kitchen Workshop': ['Kitchen Workshop Trainee'],
  'Account Manager': ['Senior Account Manager'],
  'Accountant': ['Chief Accountant'],
  'Store Manager': ['Store Manager Trainee'],
  'Physician': ['Chief Physician'],
  'Early Childhood Special Education Teacher': [
    'Early Childhood Education Special Education Teacher',
    'Special Education Teacher in Early Childhood Education',
    'Special Teacher in Early Childhood Education',
  ],
  'Swedish and German Teacher': ['Teacher of Swedish and German'],
  'Electrical Automation Technician': ['Electrical and Automation Technician'],
  'Instructor': ['Ohjaaja', 'Vastaava ohjaaja'],
  'Developer': ['Lead Developer', 'Senior Developer'],
  'Early Childhood Education Social Pedagogue': ['Social Pedagogue in Early Childhood Education'],
  'Waiter': ['Ravintolan tarjoilija', 'Tarjoilija', 'À la carte tarjoilija'],
  'Carpenter': ['Kirvesmies'],
  'Interior Carpenter': ['Sisustuskirvesmies'],
  'Taxi Driver': ['Taksinkuljettaja'],
  'Truck Driver': ['Kuorma-autonkuljettaja'],
  'Bus Driver': ['Linja-autonkuljettaja'],
  'Home Cleaner': ['Kotisiivooja'],
  'Vehicle Technician': ['Ajoneuvoasentaja'],
  'Car Mechanic': ['Autonasentaja', 'Haemme ammattilaista ja kokenutta autoasentajaa'],
  'Maintenance Technician': ['Huoltoasentaja', 'Kunnossapitoasentaja'],
  'Construction Cleaner': ['Rakennussiivooja'],
  'Room Attendant': ['Kerroshoitaja'],
  'Scaffolder': ['Telineasentaja'],
  'Breakfast Cook': ['Aamiaiskokki'],
  'Cook': ['Lounaskokki'],
  'Roofer': ['Kattoasentaja'],
  'Concrete Element Worker': ['Elementtiasentaja', 'Elementtityöntekijä'],
  'Dental Nurse': ['Hammashoitaja'],
  'Stone Installer': ['Kiviasentaja'],
  'Handicraft Workshop': ['Kädentaitopaja'],
  'Move-Out Cleaner': ['Loppusiivooja'],
  'Tire Fitter': ['Rengasasentaja'],
  'Telecommunications Installer': ['Tietoliikenneasentaja Uudellemaalle'],
  'Early Childhood Education Childcare Worker': ['Varhaiskasvatuksen lastenhoitaja'],
  'Telehandler Driver': ['Kurottajakuljettaja'],
  'Facility Cleaner': ['Laitossiivooja'],
  'Demolition Worker': ['Purkutyöntekijä'],
  'Ventilation Installer': ['IV-asentaja', 'Ilmastointiasentaja'],
  'City Manager': ['Kaupunginjohtaja'],
  'Assembler': ['Kokoonpanoasentaja'],
  'Mechanical Fitter': ['Koneasentaja'],
  'Laboratory Nurse': [
    'Laboratoriohoitaja Diagnostiikkakeskus Kliininen kemia',
    'Laboratoriohoitaja Diagnostiikkakeskus Preanalytiikka',
  ],
  'Mechanical Designer': ['Mekaniikkasuunnittelija'],
  'Stairwell Cleaner': ['Porrassiivooja'],
  'Press Brake Operator': ['Särmääjä'],
  'Hourly Teacher': ['Tuntiopettaja'],
};

function buildLowercaseLookup(canonicalToVariants: Record<string, string[]>): Record<string, string> {
  const map: Record<string, string> = {};
  for (const [canonical, variants] of Object.entries(canonicalToVariants)) {
    for (const variant of variants) {
      map[variant.trim().toLowerCase()] = canonical;
    }
  }
  return map;
}

const SALARY_TITLE_LOOKUP = buildLowercaseLookup(SALARY_ROLE_CANONICAL_ALIASES);

/**
 * Canonicalizes a raw `normalisedTitle_en` before it's used as a salary aggregation bucket, so
 * known synonym/leak variants merge into one /salary/{slug} page instead of fragmenting. Matching
 * is case-insensitive; titles with no known canonical mapping pass through unchanged.
 */
export function resolveCanonicalSalaryTitle(title: string): string {
  const key = title.trim().toLowerCase();
  return SALARY_TITLE_LOOKUP[key] ?? title;
}

/**
 * All known title strings for the same role as `title` — the canonical name plus every variant
 * merged into it (or just `[title]` unchanged if it's not part of any known group). For matching
 * against the Typesense search index, which still stores each job's raw, un-merged title (this
 * alias map only affects the salary-stats pipeline, not indexing) — a "similar jobs" or other
 * title-based search has to OR across every variant to find the postings this canonicalization
 * now considers the same occupation, not just the one exact string the current job happens to use.
 * Always includes the input title itself, so callers get a safe single-element array for titles
 * outside any known group instead of an empty result.
 */
export function getSalaryRoleTitleVariants(title: string): string[] {
  const canonical = resolveCanonicalSalaryTitle(title);
  const variants = SALARY_ROLE_CANONICAL_ALIASES[canonical] ?? [];
  return [...new Set([canonical, title, ...variants])];
}

/**
 * The other titles merged into this canonical role — for showing "Also known as: Healthcare
 * Assistant, Nurse Assistant, ..." on the /salary/{role} page, so a visitor who searched under a
 * different title than the page's own canonical name can see they landed on the right page.
 * Excludes the canonical itself (unlike getSalaryRoleTitleVariants, which is for search matching
 * and needs it included) — empty when `canonicalTitle` isn't a known canonical or has no variants.
 */
export function getOtherSalaryRoleTitles(canonicalTitle: string): string[] {
  return SALARY_ROLE_CANONICAL_ALIASES[canonicalTitle] ?? [];
}

/**
 * Legacy EN slugs that no longer have their own SalaryStatistics row now that
 * resolveCanonicalSalaryTitle merges them into a canonical bucket — e.g. "healthcare-assistant"
 * merges into "practical-nurse". Used by +data.server.ts to 301 an already-indexed legacy URL to
 * its new canonical page instead of letting it 404. Computed from the same alias map that drives
 * aggregation, so it can never drift out of sync with what actually got merged.
 */
export function legacySalaryRoleEnSlugRedirects(): Record<string, string> {
  const redirects: Record<string, string> = {};
  for (const [canonical, variants] of Object.entries(SALARY_ROLE_CANONICAL_ALIASES)) {
    const canonicalSlug = slugifyRole(canonical);
    for (const variant of variants) {
      const legacySlug = slugifyRole(variant);
      if (legacySlug && legacySlug !== canonicalSlug) {
        redirects[legacySlug] = canonicalSlug;
      }
    }
  }
  return redirects;
}
